import React, { useState, useEffect } from 'react';
import SiteFooter from './common/SiteFooter';
import { getFeatures, isTermsV70NoticePeriod, isTermsV71NoticePeriod, isTermsV80NoticePeriod, isTermsV80Announced } from '../utils/features';
import { V70_BANNER_TEXT, V70_BANNER_LINK } from '../config/termsV70';
import { V71_BANNER_TEXT, V71_BANNER_LINK, V71_BANNER_SHORT_TITLE } from '../config/termsV71';   // 약관 v7.1 CNC 파트너 개정 공고(10-24 공고·11-24 시행)
import { v80BannerText, V80_BANNER_LINK, v80BannerShortTitle } from '../config/termsV80';   // 약관 v8.0 전면 정비 공고 — 공고 전(config/features.termsV80NoticeAt·termsV80At 없으면 숨김)
import { V328_BANNER_TEXT, V328_BANNER_LINK, V328_LOGIN_TITLE, V328_DISMISS_KEY_PREFIX, isPrivacyV328BannerPeriod } from '../config/privacyV328';   // 처리방침 v3.2.8 공고(10-16 공고·10-23 시행)
import { V335_BANNER_TEXT, V335_BANNER_LINK, V335_LOGIN_TITLE, V335_DISMISS_KEY_PREFIX, isPrivacyV335BannerPeriod } from '../config/privacyV335';   // 처리방침 v3.3.5 공고(10-27 공고·11-03 시행, S5)
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import { auth } from '../firebase/config';
import { Mail, Lock, AlertCircle, LogIn, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ForgotPassword from './ForgotPassword';

function Login({ onLogin, onShowRegister }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [showNotice, setShowNotice] = useState(() => {
        // 오늘 하루 안 보기 체크한 경우
        const dismissed = localStorage.getItem('dc_notice_dismissed');
        if (dismissed) {
            const dismissedDate = new Date(dismissed);
            const now = new Date();
            if (now.getFullYear() === dismissedDate.getFullYear() && now.getMonth() === dismissedDate.getMonth() && now.getDate() === dismissedDate.getDate()) {
                return false;
            }
        }
        // 2026-04-20까지만 표시 (한달)
        return new Date() < new Date('2026-04-20');
    });
    const navigate = useNavigate();

    // 📢 약관 v7.0 개정 공고 띠 (비로그인 회원 고지 — 초안 §3-2 "표시 위치 추가: 로그인 화면"). 게시 2026-10-06 ~ 시행(2026-11-06, config/features.termsV70At) 전까지.
    //   문안 src/config/termsV70.js V70_BANNER_TEXT. 닫기는 브라우저 단위(localStorage policyBannerV70Dismissed_login). 훅은 early return 위.
    const [policyFeatures, setPolicyFeatures] = useState(null);
    const [policyV70Open, setPolicyV70Open] = useState(false);
    const [policyV70Dismissed, setPolicyV70Dismissed] = useState(() => {
        try { return localStorage.getItem('policyBannerV70Dismissed_login') === '1'; }
        catch (e) { console.error('[약관 v7.0 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e); return false; }
    });
    useEffect(() => {
        let alive = true;
        getFeatures()
            .then(f => { if (alive) setPolicyFeatures(f || {}); })
            .catch(e => { console.error('[약관 v7.0 공고] 플래그 읽기 실패 — 기본 시행일 사용:', e?.message || e); if (alive) setPolicyFeatures({}); });
        return () => { alive = false; };
    }, []);
    // ⛔ 2026-10-28 대표 결정 「약관 띠는 없애줘」(증빙 위험 안내 후 「전부 제거」 선택) — v7.0·v7.1 띠 미표시. /terms 공고는 그대로. 되돌리려면 TERMS_BANNERS_OFF=false.
    const TERMS_BANNERS_OFF = true;
    const policyV70Show = !TERMS_BANNERS_OFF && isTermsV70NoticePeriod(policyFeatures) && !policyV70Dismissed;
    const dismissPolicyV70 = () => {
        setPolicyV70Dismissed(true);
        try { localStorage.setItem('policyBannerV70Dismissed_login', '1'); }
        catch (e) { console.error('[약관 v7.0 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };
    // 📢 약관 v7.1 개정 공고 띠 — v7.0 띠와 같은 접힘 방식. 게시 2026-10-24 ~ 시행(2026-11-24, config/features.termsV71At) 전까지. 문안 src/config/termsV71.js. 닫기 키 v7.1 전용(브라우저 단위). 훅은 early return 위.
    const [policyV71Open, setPolicyV71Open] = useState(false);
    const [policyV71Dismissed, setPolicyV71Dismissed] = useState(() => {
        try { return localStorage.getItem('policyBannerV71Dismissed_login') === '1'; }
        catch (e) { console.error('[약관 v7.1 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e); return false; }
    });
    //   v8.0 공고가 시작되면(isTermsV80Announced) v7.1 띠는 숨긴다 — v7.1 공고는 v8.0 공고로 대체
    const policyV71Show = !TERMS_BANNERS_OFF && isTermsV71NoticePeriod(policyFeatures) && !isTermsV80Announced(policyFeatures) && !policyV71Dismissed;
    const dismissPolicyV71 = () => {
        setPolicyV71Dismissed(true);
        try { localStorage.setItem('policyBannerV71Dismissed_login', '1'); }
        catch (e) { console.error('[약관 v7.1 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };
    // 📢 약관 v8.0 전면 정비 공고 띠 — ★ 공고 전. 게시 = config/features.termsV80NoticeAt ~ termsV80At(둘 다 있어야 그린다). 문안 src/config/termsV80.js. 닫기 키 v8.0 전용(브라우저 단위). 훅은 early return 위.
    const [policyV80Open, setPolicyV80Open] = useState(false);
    const [policyV80Dismissed, setPolicyV80Dismissed] = useState(() => {
        try { return localStorage.getItem('policyBannerV80Dismissed_login') === '1'; }
        catch (e) { console.error('[약관 v8.0 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e); return false; }
    });
    const policyV80Text = v80BannerText(policyFeatures);
    const policyV80Show = isTermsV80NoticePeriod(policyFeatures) && !!policyV80Text && !policyV80Dismissed;
    const dismissPolicyV80 = () => {
        setPolicyV80Dismissed(true);
        try { localStorage.setItem('policyBannerV80Dismissed_login', '1'); }
        catch (e) { console.error('[약관 v8.0 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };

    // 📢 개인정보처리방침 v3.2.8 공고 한 줄 (문안 docs/공고문안_처리방침v3.2.8_2026-10-16.md §3-2) — 게시 2026-10-16 ~ 2026-10-30, 시행(10-23) 전에는 시각으로 내려가지 않는다.
    //   접힌 한 줄 → [내용 보기]로 §3-1 문구. 닫기 키 v3.2.8 전용(policyBannerV328Dismissed_login, 브라우저 단위). 훅은 early return 위.
    const [policyV328Open, setPolicyV328Open] = useState(false);
    const [policyV328Dismissed, setPolicyV328Dismissed] = useState(() => {
        try { return localStorage.getItem(`${V328_DISMISS_KEY_PREFIX}login`) === '1'; }
        catch (e) { console.error('[처리방침 v3.2.8 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e); return false; }
    });
    const policyV328Show = isPrivacyV328BannerPeriod() && !policyV328Dismissed;
    const dismissPolicyV328 = () => {
        setPolicyV328Dismissed(true);
        try { localStorage.setItem(`${V328_DISMISS_KEY_PREFIX}login`, '1'); }
        catch (e) { console.error('[처리방침 v3.2.8 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };
    // 📢 처리방침 v3.3.5 공고 한 줄 (S5) — 게시 2026-10-27 ~ 2026-11-10, v3.2.8 과 같은 접힘 방식. 닫기 키 v3.3.5 전용(policyBannerV335Dismissed_login). 훅은 early return 위.
    const [policyV335Open, setPolicyV335Open] = useState(false);
    const [policyV335Dismissed, setPolicyV335Dismissed] = useState(() => {
        try { return localStorage.getItem(`${V335_DISMISS_KEY_PREFIX}login`) === '1'; }
        catch (e) { console.error('[처리방침 v3.3.5 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e); return false; }
    });
    const policyV335Show = isPrivacyV335BannerPeriod() && !policyV335Dismissed;
    const dismissPolicyV335 = () => {
        setPolicyV335Dismissed(true);
        try { localStorage.setItem(`${V335_DISMISS_KEY_PREFIX}login`, '1'); }
        catch (e) { console.error('[처리방침 v3.3.5 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };

    // 회원가입 페이지로 이동
    const handleGoToSignup = () => {
        if (onShowRegister) {
            onShowRegister();
        } else {
            navigate('/signup');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email || !password) {
            setError('이메일과 비밀번호를 입력해주세요.');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            // ✅ 자동 로그인 설정
            const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
            await setPersistence(auth, persistence);
            
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            
            if (onLogin && typeof onLogin === 'function') {
                onLogin(userCredential.user);
            } else {
                window.location.reload();
            }
            
        } catch (error) {
            console.error('로그인 실패:', error);
            
            switch (error.code) {
                case 'auth/user-not-found':
                    setError('등록되지 않은 이메일입니다.');
                    break;
                case 'auth/wrong-password':
                    setError('비밀번호가 올바르지 않습니다.');
                    break;
                case 'auth/invalid-email':
                    setError('올바른 이메일 형식이 아닙니다.');
                    break;
                case 'auth/invalid-credential':
                    setError('이메일 또는 비밀번호가 올바르지 않습니다.');
                    break;
                case 'auth/too-many-requests':
                    setError('로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.');
                    break;
                default:
                    setError('로그인에 실패했습니다. 다시 시도해주세요.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (showForgotPassword) {
        return (
            <ForgotPassword 
                onBack={() => setShowForgotPassword(false)}
            />
        );
    }

    return (
        <div style={styles.container}>
            {/* 배경 — NASA 허블 성단 사진 (퍼블릭 도메인) */}
            <img src="/images/space_bg.jpg" alt="" style={styles.bgImage} />
            <div style={styles.bgOverlay} />
            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            {/* 안내서 모달 — 비활성화 */}
            {false && showNotice && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: 16, maxWidth: 800, width: '95%', maxHeight: '92vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>📋 덴탈클러스터 안내</h3>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>~2026.04.20</span>
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
                            <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 800, color: '#1e3a5f' }}>
                                [안내] 치과기공소 전용 주문관리 플랫폼 <span style={{ color: '#3b82f6' }}>DentalCluster</span>
                            </h2>
                            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>기공소 소장님들께 드립니다</p>

                            <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.8 }}>
                                <p>안녕하세요, DentalCluster 운영팀입니다.</p>
                                <p>치과기공소를 운영하시면서 이런 불편함, 한 번쯤 겪어보셨을 것 같습니다.</p>
                                <p style={{ padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: 8, color: '#64748b', fontSize: 13, lineHeight: 1.7 }}>
                                    카톡으로 주문받고, 엑셀로 정리하고, 월말에 명세서 수기로 끊고…<br/>
                                    어느 치과에서 리메이크가 많이 나오는지도 감으로만 알고 계시지 않으신가요?
                                </p>
                                <p>저도 8년째 치과기공소를 직접 운영하면서 똑같은 불편함을 겪었습니다.<br/>그래서 <strong>직접 만들었습니다.</strong></p>
                                <p><strong>DentalCluster는 치과기공소를 위한 디지털 주문 관리 플랫폼입니다.</strong></p>

                                <div style={{ padding: '14px 16px', backgroundColor: '#eef2ff', borderRadius: 10, marginBottom: 16 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {['치과에서 앱으로 직접 주문 → 전자 의뢰서 자동 전달',
                                          '주문 목록 한눈에 확인 · 진행 상태 실시간 업데이트',
                                          '거래명세서 자동 생성 — 월말 수기 작업 끝',
                                          '기공소별 리메이크율 통계 — 데이터로 품질 관리',
                                          '기공소 홍보 페이지 — 치과가 직접 검색하고 연락'
                                        ].map((t, i) => (
                                            <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#334155' }}>
                                                <span style={{ color: '#6366f1', fontWeight: 700 }}>✔</span> {t}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <p><strong>가입 방법은 간단합니다.</strong></p>
                                <div style={{ padding: '14px 16px', backgroundColor: '#f0fdf4', borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
                                    <div style={{ marginBottom: 6 }}>① <strong>www.dentalcluster.com</strong> 접속</div>
                                    <div style={{ marginBottom: 6 }}>② 사업자 정보 입력 후 가입 신청 (관리자 확인 뒤 승인)</div>
                                    <div>③ 설정 {'>'} 기공소 홍보 페이지 작성 완료</div>
                                </div>

                                <p>이후 저희가 치과에 기공소를 홍보해드립니다.</p>

                                <p>궁금하신 점은 아래로 편하게 연락 주세요.</p>
                                <div style={{ padding: '14px 16px', backgroundColor: '#f8fafc', borderRadius: 10, fontSize: 13, lineHeight: 2 }}>
                                    📞 <strong>010-2972-3733</strong><br/>
                                    💬 카카오톡 : <strong>DentalCluster</strong><br/>
                                    🌐 <strong>www.dentalcluster.com</strong>
                                </div>

                                <p style={{ marginTop: 16, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                                    치과기공소 운영하시는 소장님들의 많은 관심 부탁드립니다.<br/>감사합니다.
                                </p>
                            </div>
                        </div>
                        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', cursor: 'pointer' }}>
                                <input type="checkbox" onChange={(e) => { if (e.target.checked) localStorage.setItem('dc_notice_dismissed', new Date().toISOString()); }} />
                                오늘 하루 안 보기
                            </label>
                            <button onClick={() => setShowNotice(false)} style={{ padding: '8px 24px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div style={styles.content}>
                {/* 왼쪽 브랜드 카피 (capa.ai 히어로 텍스트 스타일) */}
                <div style={styles.hero}>
                    <h1 style={styles.heroTitle}>
                        치과와 기공소를 잇다,<br />덴탈클러스터
                    </h1>
                    <p style={styles.heroSubtitle}>
                        주문·의뢰서·거래명세서·리메이크 분석까지<br />
                        치과기공 업무를 하나의 플랫폼에서.
                    </p>
                </div>

                <div style={styles.loginBox}>
                {/* 📢 약관 v8.0 전면 정비 공고 띠 — 공고 전(features 값 없음)엔 숨김. v7.1 띠와 같은 접힘 방식, 시행 전까지만, 닫기 가능. 맨 위 */}
                {policyV80Show && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#3B4468', lineHeight: 1.6 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            {policyV80Open ? policyV80Text : <b>{v80BannerShortTitle(policyFeatures)}</b>}
                            {!policyV80Open && (
                                <button type="button" onClick={() => setPolicyV80Open(true)} style={{ background: 'none', border: 'none', padding: '0 8px 0 0', color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                    {'내용 보기 ▾'}
                                </button>
                            )}
                            <button type="button" onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                                {V80_BANNER_LINK}
                            </button>
                        </span>
                        <button type="button" onClick={dismissPolicyV80} title="닫기" aria-label="닫기"
                            style={{ background: 'none', border: 'none', padding: 0, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1, fontSize: 16 }}>
                            {'×'}
                        </button>
                    </div>
                )}
                {/* 📢 약관 v7.1 개정 공고 띠 — v7.0 띠와 같은 접힘 방식, 시행(11-24) 전까지만(v8.0 공고 시작 시 숨김), 닫기 가능. v7.0 띠 위에 둔다 */}
                {policyV71Show && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#3B4468', lineHeight: 1.6 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            {policyV71Open ? V71_BANNER_TEXT : <b>{V71_BANNER_SHORT_TITLE}</b>}
                            {!policyV71Open && (
                                <button type="button" onClick={() => setPolicyV71Open(true)} style={{ background: 'none', border: 'none', padding: '0 8px 0 0', color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                    {'내용 보기 ▾'}
                                </button>
                            )}
                            <button type="button" onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                                {V71_BANNER_LINK}
                            </button>
                        </span>
                        <button type="button" onClick={dismissPolicyV71} title="닫기" aria-label="닫기"
                            style={{ background: 'none', border: 'none', padding: 0, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1, fontSize: 16 }}>
                            {'×'}
                        </button>
                    </div>
                )}
                {/* 📢 약관 v7.0 개정 공고 띠 — 기공소·치과 로그인 화면, 시행 전까지만, 닫기 가능 */}
                {policyV70Show && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#3B4468', lineHeight: 1.6 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            {/* 10-08 대표 "안내문이 너무 많으면 보기 안 좋다" — 기본은 한 줄 제목, [내용 보기]로 공고 원문 그대로 펼침 */}
                            {policyV70Open ? V70_BANNER_TEXT : <b>{'이용약관 개정 안내 (v7.0 · 2026-11-06 시행) '}</b>}
                            {!policyV70Open && (
                                <button type="button" onClick={() => setPolicyV70Open(true)} style={{ background: 'none', border: 'none', padding: '0 8px 0 0', color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                    {'내용 보기 ▾'}
                                </button>
                            )}
                            <button type="button" onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                                {V70_BANNER_LINK}
                            </button>
                        </span>
                        <button type="button" onClick={dismissPolicyV70} title="닫기" aria-label="닫기"
                            style={{ background: 'none', border: 'none', padding: 0, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1, fontSize: 16 }}>
                            {'×'}
                        </button>
                    </div>
                )}
                {/* 📢 처리방침 v3.2.8 개정 공고 한 줄 — v7.0 띠와 같은 접힘 방식(§3-2) */}
                {policyV328Show && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#3B4468', lineHeight: 1.6 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            {policyV328Open ? V328_BANNER_TEXT : <b>{V328_LOGIN_TITLE}</b>}
                            {!policyV328Open && (
                                <button type="button" onClick={() => setPolicyV328Open(true)} style={{ background: 'none', border: 'none', padding: '0 8px 0 0', color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                    {'내용 보기'}
                                </button>
                            )}
                            {policyV328Open && (
                                <button type="button" onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {V328_BANNER_LINK}
                                </button>
                            )}
                        </span>
                        <button type="button" onClick={dismissPolicyV328} title="닫기" aria-label="닫기"
                            style={{ background: 'none', border: 'none', padding: 0, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1, fontSize: 16 }}>
                            {'×'}
                        </button>
                    </div>
                )}
                {/* 📢 처리방침 v3.3.5 개정 공고 한 줄 (S5) — v3.2.8 과 같은 접힘 방식 */}
                {policyV335Show && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#3B4468', lineHeight: 1.6 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            {policyV335Open ? V335_BANNER_TEXT : <b>{V335_LOGIN_TITLE}</b>}
                            {!policyV335Open && (
                                <button type="button" onClick={() => setPolicyV335Open(true)} style={{ background: 'none', border: 'none', padding: '0 8px 0 0', color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                    {'내용 보기'}
                                </button>
                            )}
                            {policyV335Open && (
                                <button type="button" onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {V335_BANNER_LINK}
                                </button>
                            )}
                        </span>
                        <button type="button" onClick={dismissPolicyV335} title="닫기" aria-label="닫기"
                            style={{ background: 'none', border: 'none', padding: 0, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1, fontSize: 16 }}>
                            {'×'}
                        </button>
                    </div>
                )}
                <div style={styles.header}>
                    <div style={styles.iconWrapper}>
                        <LogIn size={32} color="#3b82f6" />
                    </div>
                    <h1 style={styles.title}>로그인</h1>
                    <p style={styles.subtitle}>계정에 로그인하세요</p>
                </div>

                {error && (
                    <div style={styles.errorMessage}>
                        <AlertCircle size={18} />
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            <Mail size={16} />
                            이메일
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="example@email.com"
                            style={styles.input}
                            disabled={loading}
                            autoFocus
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            <Lock size={16} />
                            비밀번호
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            style={styles.input}
                            disabled={loading}
                        />
                    </div>

                    {/* ✅ 자동 로그인 체크박스 */}
                    <div style={styles.rememberMeRow}>
                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={styles.checkbox}
                            />
                            <span style={styles.checkboxText}>자동 로그인</span>
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            style={styles.linkButton}
                        >
                            <KeyRound size={14} />
                            비밀번호 찾기
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            ...styles.submitButton,
                            ...(loading ? styles.submitButtonDisabled : {})
                        }}
                    >
                        {loading ? (
                            <>
                                <div style={styles.spinner}></div>
                                로그인 중...
                            </>
                        ) : (
                            <>
                                <LogIn size={18} />
                                로그인
                            </>
                        )}
                    </button>
                </form>

                <div style={styles.footer}>
                    <p style={styles.footerText}>
                        계정이 없으신가요?{' '}
                        <button
                            type="button"
                            onClick={handleGoToSignup}
                            style={styles.registerLink}
                        >
                            회원가입
                        </button>
                    </p>
                </div>
                </div>
            </div>
        <div style={{ position: 'relative', zIndex: 2, margin: '0 -20px' }}><SiteFooter /></div>
        </div>
    );
}

const styles = {
    container: {
        // 10-08 대표 지적 "로그인 화면이 이상" — SiteFooter 가 가로 flex 항목으로 들어가 히어로·로그인 상자가 좁아져 세로로 쌓이고 위가 잘림.
        //  → 세로 방향(column): 위 = 히어로+로그인(content, 남는 높이 채움·가운데), 아래 = 푸터(전체 폭)
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        // 영상이 없거나 로드 실패 시 보이는 대체 배경
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #2563eb 100%)',
        padding: '20px 20px 0',
        overflowX: 'hidden',
    },
    bgImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        zIndex: 0,
    },
    bgOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        // 텍스트 가독성용 어두운 오버레이
        background: 'linear-gradient(90deg, rgba(2,6,23,0.75) 0%, rgba(2,6,23,0.45) 55%, rgba(2,6,23,0.35) 100%)',
        zIndex: 1,
    },
    content: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '64px',
        flexWrap: 'wrap',
        width: '100%',
        maxWidth: '1100px',
        flex: '1 0 auto',
        margin: '0 auto',
        padding: '24px 0',
    },
    hero: {
        flex: '1 1 380px',
        minWidth: '300px',
        color: '#fff',
    },
    heroTitle: {
        margin: '0 0 20px 0',
        fontSize: 'clamp(28px, 4vw, 46px)',
        fontWeight: '800',
        lineHeight: 1.25,
        color: '#fff',
        textShadow: '0 2px 12px rgba(0,0,0,0.4)',
    },
    heroSubtitle: {
        margin: 0,
        fontSize: 'clamp(15px, 1.6vw, 18px)',
        lineHeight: 1.7,
        color: 'rgba(255,255,255,0.85)',
        textShadow: '0 1px 8px rgba(0,0,0,0.4)',
    },
    loginBox: {
        flex: '0 1 440px',
        width: '100%',
        maxWidth: '440px',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: '16px',
        padding: '48px 40px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
    },
    header: {
        textAlign: 'center',
        marginBottom: '32px',
    },
    iconWrapper: {
        width: '64px',
        height: '64px',
        margin: '0 auto 16px',
        borderRadius: '50%',
        backgroundColor: '#eff6ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        margin: '0 0 8px 0',
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
    },
    subtitle: {
        margin: 0,
        fontSize: '15px',
        color: '#64748b',
    },
    errorMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 16px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: '1px solid #ef4444',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '14px',
        fontWeight: '500',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
    },
    input: {
        padding: '14px 16px',
        fontSize: '15px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        transition: 'all 0.2s',
        outline: 'none',
    },
    // ✅ 자동 로그인 스타일
    rememberMeRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '-8px',
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
    },
    checkbox: {
        width: '18px',
        height: '18px',
        cursor: 'pointer',
        accentColor: '#3b82f6',
    },
    checkboxText: {
        fontSize: '14px',
        color: '#475569',
        fontWeight: '500',
    },
    linkButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        background: 'none',
        border: 'none',
        color: '#3b82f6',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
    },
    submitButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '16px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        marginTop: '8px',
    },
    submitButtonDisabled: {
        backgroundColor: '#9ca3af',
        cursor: 'not-allowed',
    },
    spinner: {
        width: '18px',
        height: '18px',
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderTop: '3px solid white',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    footer: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid #e5e7eb',
        textAlign: 'center',
    },
    footerText: {
        margin: 0,
        fontSize: '14px',
        color: '#64748b',
    },
    registerLink: {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#3b82f6',
        fontWeight: '600',
        cursor: 'pointer',
        padding: '8px 12px',
        fontSize: '14px',
        borderRadius: '6px',
        transition: 'background-color 0.2s',
    },
};

export default Login;